/**
 * Enhanced LeaderboardPage using the new component system
 * Demonstrates student ranking and performance analytics with enhanced filtering
 */

import React, { useEffect, useState } from 'react';
import { FiAward, FiTrendingUp, FiUsers } from 'react-icons/fi';

// New component system imports
import {
  Avatar,
  Badge,
  HStack,
  Progress,
  Select,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  AdminLayout,
  Card,
  DataTable,
  StatsCard,
  TableColumn,
} from '@grcs/components';

// Existing hooks and types
import { useData } from '@grcs/context/DataContext';
import {
  useClassOperations,
  useStudentOperations,
} from '@grcs/hook/useDatabase';
import { useTableSelectedData } from '@grcs/hook/useTableData';
import {
  ClassProfile,
  Semester,
  StudentProfile,
  StudentWithAnalytics,
} from '@grcs/types/DataTypes';
import { TABLE_NAMES } from '@grcs/utils/database/types';

interface LeaderboardStats {
  totalStudents: number;
  totalClasses: number;
  topPerformer: string | null;
  averageCompletion: number;
}

interface CachedLeaderboardData {
  data: RankedStudent[];
  stats: LeaderboardStats;
  classes: ClassProfile[];
  timestamp: number;
  semester: string;
}

interface RankedStudent extends StudentWithAnalytics {
  rank: number;
  performance_level: 'excellent' | 'good' | 'average' | 'needs_improvement';
  grade?: string;
  // Enhanced reading analytics
  reading_efficiency: number;
  focus_score: number;
  time_consistency: number;
  engagement_level: 'high' | 'medium' | 'low';
}

export const LeaderboardPageEnhanced: React.FC = () => {
  const { supabase } = useData();
  const classOps = useClassOperations();
  const studentOps = useStudentOperations();

  const { data: semesterData } = useTableSelectedData(
    TABLE_NAMES.SEMESTER,
    'semester_year, is_current',
    'semester_year',
  );

  const [semesters, setSemesters] = useState<Semester[]>([]);
  const [activeSemester, setActiveSemester] = useState<string>('');
  const [classes, setClasses] = useState<ClassProfile[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<string>('all');
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [studentsWithAnalytics, setStudentsWithAnalytics] = useState<
    RankedStudent[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<LeaderboardStats>({
    totalStudents: 0,
    totalClasses: 0,
    topPerformer: null,
    averageCompletion: 0,
  });

  // Optimized bulk data fetching with caching
  const fetchLeaderboardData = async (semester: string) => {
    if (!semester) {
      console.log(
        '⏭️ Skipping leaderboard data loading - no semester provided',
      );
      setError(
        'No active semester available. Please create and activate a semester first.',
      );
      setIsLoading(false);
      return;
    }

    console.log(`📊 Loading leaderboard data for semester: ${semester}`);
    setError(null); // Clear any previous errors

    // Check cache first
    const cacheKey = `leaderboard_${semester}`;
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      try {
        const cachedData: CachedLeaderboardData = JSON.parse(cached);
        const isExpired = Date.now() - cachedData.timestamp > 5 * 60 * 1000; // 5 minutes

        if (!isExpired && cachedData.semester === semester) {
          console.log('Loading leaderboard data from cache');
          setStudentsWithAnalytics(cachedData.data);
          setStats(cachedData.stats);
          setClasses(cachedData.classes);
          setIsLoading(false);
          return;
        }
      } catch (e) {
        console.log('Failed to parse cached leaderboard data');
      }
    }

    setIsLoading(true);
    const startTime = performance.now();

    try {
      // Step 1: Load classes for the semester
      const { data: classData } = await classOps.getClassesBySemester(semester);
      const classList = classData || [];
      console.log(
        'Classes found:',
        classList.length,
        classList.map((c) => c.class_name),
      );
      setClasses(classList);

      if (classList.length === 0) {
        console.log('No classes found for semester:', semester);
        const emptyData = {
          data: [],
          stats: {
            totalStudents: 0,
            totalClasses: 0,
            topPerformer: null,
            averageCompletion: 0,
          },
          classes: [],
          timestamp: Date.now(),
          semester: semester,
        };

        setStudentsWithAnalytics([]);
        setStats(emptyData.stats);
        sessionStorage.setItem(cacheKey, JSON.stringify(emptyData));
        return;
      }

      // Step 2: Get all students for the semester (optimized but safe approach)
      const allStudents: (StudentProfile & { grade: string })[] = [];

      // Use Promise.all to fetch students concurrently instead of sequentially
      const studentPromises = classList.map(async (classItem: ClassProfile) => {
        const { data } = await studentOps.getStudentsByClassId(
          classItem.class_token,
        );
        if (data) {
          return (data as StudentProfile[]).map((student: StudentProfile) => ({
            ...student,
            grade: classItem.grade,
            class_token: classItem.class_token,
          }));
        }
        return [];
      });

      const studentResults = await Promise.all(studentPromises);
      console.log(
        'Student results from Promise.all:',
        studentResults.map((r) => r.length),
      );

      studentResults.forEach((students) => {
        allStudents.push(...students);
      });

      console.log('Total students found:', allStudents.length);
      console.log(
        'First few students:',
        allStudents.slice(0, 3).map((s) => ({
          name: `${s.first_name} ${s.last_name}`,
          grade: s.grade,
          class_token: s.class_token,
        })),
      );

      if (allStudents.length === 0) {
        console.log(
          'No students found for classes:',
          classList.map((c) => c.class_name),
        );
        const emptyData = {
          data: [],
          stats: {
            totalStudents: 0,
            totalClasses: classList.length,
            topPerformer: null,
            averageCompletion: 0,
          },
          classes: classList,
          timestamp: Date.now(),
          semester: semester,
        };

        setStudentsWithAnalytics([]);
        setStats(emptyData.stats);
        sessionStorage.setItem(cacheKey, JSON.stringify(emptyData));
        return;
      }

      // Step 3: Fetch homework data for students using direct database queries
      console.log('Fetching analytics for', allStudents.length, 'students');

      // Get all student tokens for bulk query
      const studentTokens = allStudents.map(
        (s: StudentProfile) => s.student_token,
      );

      // Bulk query for homework data with enhanced fields
      const { data: allHomeworkData, error: homeworkError } = await supabase
        .from('homework_student_relation')
        .select(
          'student_token, total_time, question_attempted, created_at, updated_at',
        )
        .in('student_token', studentTokens);

      if (homeworkError) {
        console.error('Error fetching homework data:', homeworkError);
      }

      console.log(
        'Homework data returned for',
        allHomeworkData?.length || 0,
        'records',
      );

      // Process analytics in memory
      const studentHomeworkMap = new Map<string, any[]>();

      if (allHomeworkData) {
        allHomeworkData.forEach((homework: any) => {
          if (!studentHomeworkMap.has(homework.student_token)) {
            studentHomeworkMap.set(homework.student_token, []);
          }
          studentHomeworkMap.get(homework.student_token)!.push(homework);
        });
      }

      // Calculate enhanced analytics for each student
      const studentsWithAnalytics = allStudents.map(
        (student: StudentProfile & { grade: string }) => {
          const homeworkData =
            studentHomeworkMap.get(student.student_token) || [];

          const totalHomework = homeworkData.length;
          const completedHomework = homeworkData.filter((h: any) => {
            const attempted = h.question_attempted;
            if (typeof attempted === 'string') {
              try {
                const parsed = JSON.parse(attempted);
                return Array.isArray(parsed)
                  ? parsed.length > 0
                  : Object.keys(parsed).length > 0;
              } catch {
                return false;
              }
            }
            return attempted && attempted > 0;
          });

          const percentCompleted =
            totalHomework > 0
              ? (completedHomework.length / totalHomework) * 100
              : 0;

          // Enhanced time analytics
          const timesSpent: number[] = [];
          const sessionDurations: number[] = [];

          completedHomework.forEach((h: any) => {
            if (h.total_time) {
              timesSpent.push(h.total_time);

              // Calculate session duration if timestamps are available
              if (h.created_at && h.updated_at) {
                const sessionTime =
                  new Date(h.updated_at).getTime() -
                  new Date(h.created_at).getTime();
                if (sessionTime > 0) {
                  sessionDurations.push(sessionTime);
                }
              }
            }
          });

          const averageTime =
            timesSpent.length > 0
              ? timesSpent.reduce((sum, time) => sum + time, 0) /
                timesSpent.length
              : 0;

          const avgSessionTime =
            sessionDurations.length > 0
              ? sessionDurations.reduce((sum, time) => sum + time, 0) /
                sessionDurations.length
              : averageTime;

          // Calculate reading efficiency (completion rate vs time ratio)
          const readingEfficiency =
            averageTime > 0 && percentCompleted > 0
              ? Math.min(
                  100,
                  Math.round((percentCompleted / (averageTime / 60000)) * 2),
                ) // per minute
              : 50;

          // Focus score (active time vs session time)
          const focusScore =
            avgSessionTime > 0 && averageTime > 0
              ? Math.min(100, Math.round((averageTime / avgSessionTime) * 100))
              : 75;

          // Time consistency (lower variance = higher consistency)
          const timeConsistency =
            timesSpent.length > 1
              ? Math.max(
                  0,
                  100 -
                    Math.round(
                      (Math.sqrt(
                        timesSpent.reduce((sum, time) => {
                          const diff = time - averageTime;
                          return sum + diff * diff;
                        }, 0) / timesSpent.length,
                      ) /
                        averageTime) *
                        100,
                    ),
                )
              : 100;

          // Engagement level based on multiple factors
          let engagementLevel: 'high' | 'medium' | 'low' = 'medium';
          const engagementScore =
            (focusScore + timeConsistency + readingEfficiency) / 3;
          if (engagementScore >= 75) engagementLevel = 'high';
          else if (engagementScore < 50) engagementLevel = 'low';

          return {
            ...student,
            completed: completedHomework.length,
            total_students: totalHomework,
            percent_completed: percentCompleted,
            average_time: averageTime,
            reading_efficiency: readingEfficiency,
            focus_score: focusScore,
            time_consistency: timeConsistency,
            engagement_level: engagementLevel,
          };
        },
      );

      console.log(
        'Students with analytics calculated:',
        studentsWithAnalytics.length,
      );

      // Step 4: Rank students and add performance levels
      const rankedStudents: RankedStudent[] = studentsWithAnalytics
        .sort((a, b) => (b.percent_completed || 0) - (a.percent_completed || 0))
        .map((student, index) => {
          let performanceLevel: RankedStudent['performance_level'] =
            'needs_improvement';
          const completion = student.percent_completed || 0;

          if (completion >= 90) performanceLevel = 'excellent';
          else if (completion >= 75) performanceLevel = 'good';
          else if (completion >= 50) performanceLevel = 'average';

          return {
            ...student,
            rank: index + 1,
            performance_level: performanceLevel,
          } as RankedStudent;
        });

      // Step 5: Calculate stats
      const avgCompletion =
        rankedStudents.reduce((sum, s) => sum + (s.percent_completed || 0), 0) /
          rankedStudents.length || 0;
      const topStudent = rankedStudents[0];

      const calculatedStats = {
        totalStudents: rankedStudents.length,
        totalClasses: classList.length,
        topPerformer: topStudent
          ? `${topStudent.first_name} ${topStudent.last_name}`
          : null,
        averageCompletion: avgCompletion,
      };

      // Step 6: Cache the results
      const cacheData: CachedLeaderboardData = {
        data: rankedStudents,
        stats: calculatedStats,
        classes: classList,
        timestamp: Date.now(),
        semester: semester,
      };

      sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));

      // Step 7: Update state
      setStudentsWithAnalytics(rankedStudents);
      setStats(calculatedStats);

      const endTime = performance.now();
      console.log(
        `Leaderboard data fetched and cached in ${(endTime - startTime).toFixed(
          2,
        )}ms`,
      );
      console.log(`Total students: ${rankedStudents.length}`);
      console.log(`Available grades:`, [
        ...new Set(rankedStudents.map((s) => s.grade).filter(Boolean)),
      ]);
    } catch (error) {
      console.error('💥 Error loading leaderboard data:', error);
      setError(
        'Failed to load leaderboard data. Please try refreshing the page or contact support.',
      );
      // Reset to empty state on error
      setStudentsWithAnalytics([]);
      setStats({
        totalStudents: 0,
        totalClasses: 0,
        topPerformer: null,
        averageCompletion: 0,
      });
      setClasses([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Simplified semester loading with comprehensive error handling
  useEffect(() => {
    const loadSemesterData = async () => {
      console.log('🗓️ Loading semester data for Leaderboard...');
      if (semesterData && semesterData.length > 0) {
        const sortedSemesters = semesterData.sort(
          (a: Semester, b: Semester) =>
            parseInt(b.semester_year) - parseInt(a.semester_year),
        );
        console.log(
          'Sorted semesters:',
          sortedSemesters.map((s: Semester) => s.semester_year),
        );
        setSemesters(sortedSemesters);

        // Find current semester or default to latest
        const currentSemester =
          sortedSemesters.find((s: Semester) => s.is_current) ||
          sortedSemesters[0];
        console.log('Selected semester:', currentSemester?.semester_year);

        if (currentSemester && !activeSemester) {
          console.log(
            '✅ Setting active semester to:',
            currentSemester.semester_year,
          );
          setActiveSemester(currentSemester.semester_year);
          setError(null); // Clear any previous errors
        }
      } else {
        console.log('⚠️ No semester data available');
        setError(
          'No semesters available. Please create and activate a semester first.',
        );
        setIsLoading(false);
        // Reset to empty state
        setSemesters([]);
        setStudentsWithAnalytics([]);
        setStats({
          totalStudents: 0,
          totalClasses: 0,
          topPerformer: null,
          averageCompletion: 0,
        });
        setClasses([]);
      }
    };

    loadSemesterData();
  }, [semesterData, activeSemester]);

  // Simple data loading when semester changes
  useEffect(() => {
    console.log('useEffect triggered - activeSemester:', activeSemester);
    if (activeSemester && !error) {
      fetchLeaderboardData(activeSemester);
    } else {
      console.log('No active semester set yet or error present');
      if (!error) {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSemester, error]);

  // Filter students based on selected filters
  const filteredStudents = studentsWithAnalytics.filter((student) => {
    if (selectedGrade !== 'all' && student.grade !== selectedGrade)
      return false;
    if (selectedClass !== 'all' && student.class_token !== selectedClass)
      return false;
    return true;
  });

  // Get unique grades for filtering
  const availableGrades = [
    ...new Set(
      studentsWithAnalytics.map((s: RankedStudent) => s.grade).filter(Boolean),
    ),
  ].sort();

  // Debug logging
  console.log('Students with analytics:', studentsWithAnalytics.length);
  console.log('Available grades:', availableGrades);
  console.log(
    'First few students with grades:',
    studentsWithAnalytics
      .slice(0, 3)
      .map((s) => ({ name: `${s.first_name} ${s.last_name}`, grade: s.grade })),
  );

  // Event handlers
  const handleSemesterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSemester = e.target.value;
    console.log('🔄 Semester changed to:', newSemester);
    setActiveSemester(newSemester);
    setSelectedClass('all');
    setSelectedGrade('all');
    setError(null); // Clear any previous errors
    // Data will be fetched automatically by useEffect
  };

  // Table columns
  const leaderboardColumns: TableColumn<RankedStudent>[] = [
    {
      key: 'rank',
      header: 'Rank',
      render: (student: RankedStudent, value: number) => (
        <HStack spacing={2}>
          {value <= 3 ? (
            <FiAward
              size={20}
              color={
                value === 1 ? '#FFD700' : value === 2 ? '#C0C0C0' : '#CD7F32'
              }
            />
          ) : (
            <Text fontSize="lg" fontWeight="600">
              #{value}
            </Text>
          )}
        </HStack>
      ),
    },
    {
      key: 'first_name',
      header: 'Student',
      render: (student: RankedStudent, value: string) => (
        <HStack spacing={3}>
          <Avatar
            size="sm"
            name={`${student.first_name} ${student.last_name}`}
            bg={student.rank <= 3 ? 'yellow.400' : 'blue.500'}
          />
          <div>
            <div style={{ fontWeight: 600 }}>
              {student.first_name} {student.last_name}
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
              {student.class_name}
            </div>
          </div>
        </HStack>
      ),
    },
    {
      key: 'percent_completed',
      header: 'Completion',
      sortable: true,
      render: (student: RankedStudent, value: number) => (
        <VStack spacing={2} align="stretch" minW="120px">
          <HStack justify="space-between">
            <Badge
              colorScheme={
                value >= 90
                  ? 'green'
                  : value >= 75
                  ? 'blue'
                  : value >= 50
                  ? 'yellow'
                  : 'red'
              }
              variant="solid"
            >
              {value?.toFixed(2) || '0.00'}%
            </Badge>
          </HStack>
          <Progress
            value={value || 0}
            size="sm"
            colorScheme={
              value >= 90
                ? 'green'
                : value >= 75
                ? 'blue'
                : value >= 50
                ? 'yellow'
                : 'red'
            }
          />
        </VStack>
      ),
    },
    {
      key: 'completed',
      header: 'Assignments',
      render: (student: RankedStudent, value: number) => (
        <Text fontSize="sm">
          {value || 0} / {student.total_students || 0}
        </Text>
      ),
    },
    {
      key: 'average_time',
      header: 'Reading Metrics',
      render: (student: RankedStudent, value: number) => (
        <VStack spacing={1} align="start" minW="120px">
          <HStack spacing={2}>
            <Text fontSize="xs" color="gray.600">
              Time:
            </Text>
            <Text fontSize="sm" fontWeight="600">
              {value ? `${Math.round(value / 1000 / 60)}m` : 'N/A'}
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Text fontSize="xs" color="gray.600">
              Focus:
            </Text>
            <Badge
              size="sm"
              colorScheme={
                student.focus_score >= 75
                  ? 'green'
                  : student.focus_score >= 50
                  ? 'yellow'
                  : 'red'
              }
            >
              {student.focus_score}%
            </Badge>
          </HStack>
          <HStack spacing={2}>
            <Text fontSize="xs" color="gray.600">
              Engagement:
            </Text>
            <Badge
              size="sm"
              colorScheme={
                student.engagement_level === 'high'
                  ? 'green'
                  : student.engagement_level === 'medium'
                  ? 'blue'
                  : 'orange'
              }
            >
              {student.engagement_level}
            </Badge>
          </HStack>
        </VStack>
      ),
    },
    {
      key: 'reading_efficiency',
      header: 'Efficiency',
      render: (student: RankedStudent, value: number) => (
        <VStack spacing={1} align="center" minW="80px">
          <Text fontSize="sm" fontWeight="600">
            {value}%
          </Text>
          <Progress
            value={value}
            size="sm"
            colorScheme={value >= 75 ? 'green' : value >= 50 ? 'yellow' : 'red'}
            minW="60px"
          />
        </VStack>
      ),
    },
  ];

  // Show error state if there's an error
  if (error) {
    return (
      <AdminLayout>
        <VStack spacing={6} align="stretch">
          <div>
            <Text fontSize="2xl" fontWeight="bold" mb={2}>
              Student Leaderboard
            </Text>
            <Text fontSize="lg" color="gray.600">
              Track student performance and achievements across all classes
            </Text>
          </div>

          <Card padding={6}>
            <VStack spacing={4} py={8}>
              <Text fontSize="lg" fontWeight="600" color="red.500">
                Unable to Load Leaderboard
              </Text>
              <Text color="gray.600" textAlign="center">
                {error}
              </Text>
            </VStack>
          </Card>
        </VStack>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <div>
          <Text fontSize="2xl" fontWeight="bold" mb={2}>
            Student Leaderboard
          </Text>
          <Text fontSize="lg" color="gray.600">
            Track student performance and achievements across all classes
          </Text>
        </div>

        {/* Controls */}
        <Card padding={6}>
          <HStack spacing={6} wrap="wrap">
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" fontWeight="600">
                Semester
              </Text>
              <Select
                value={activeSemester}
                onChange={handleSemesterChange}
                minW="150px"
              >
                {semesters.map((semester) => (
                  <option
                    key={semester.semester_year}
                    value={semester.semester_year}
                  >
                    {semester.semester_year}{' '}
                    {semester.is_current && '(Current)'}
                  </option>
                ))}
              </Select>
            </VStack>

            <VStack align="start" spacing={2}>
              <Text fontSize="sm" fontWeight="600">
                Grade
              </Text>
              <Select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                minW="120px"
              >
                <option value="all">All Grades</option>
                {availableGrades.map((grade) => (
                  <option key={grade} value={grade}>
                    Grade {grade}
                  </option>
                ))}
              </Select>
            </VStack>

            <VStack align="start" spacing={2}>
              <Text fontSize="sm" fontWeight="600">
                Class
              </Text>
              <Select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                minW="150px"
              >
                <option value="all">All Classes</option>
                {classes.map((classItem) => (
                  <option
                    key={classItem.class_token}
                    value={classItem.class_token}
                  >
                    {classItem.class_name}
                  </option>
                ))}
              </Select>
            </VStack>
          </HStack>
        </Card>

        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <StatsCard
            title="Total Students"
            value={stats.totalStudents}
            icon={FiUsers}
            variant="primary"
            subtitle="Across all classes"
          />

          <StatsCard
            title="Top Performer"
            value={stats.topPerformer || 'N/A'}
            icon={FiAward}
            variant="success"
            subtitle="Highest completion rate"
          />

          <StatsCard
            title="Avg Completion"
            value={`${stats.averageCompletion.toFixed(2)}%`}
            icon={FiTrendingUp}
            variant={
              stats.averageCompletion >= 75
                ? 'success'
                : stats.averageCompletion >= 50
                ? 'warning'
                : 'danger'
            }
            subtitle="Overall performance"
          />

          <StatsCard
            title="Active Classes"
            value={stats.totalClasses}
            icon={FiAward}
            variant="info"
            subtitle={`Semester ${activeSemester}`}
          />
        </SimpleGrid>

        {/* Leaderboard Table */}
        <Card padding={6}>
          <VStack spacing={6} align="stretch">
            <HStack justify="space-between">
              <div>
                <Text fontSize="xl" fontWeight="600" mb={2}>
                  Performance Rankings
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Showing {filteredStudents.length} students
                  {(selectedGrade !== 'all' || selectedClass !== 'all') && (
                    <> with applied filters</>
                  )}
                </Text>
              </div>
            </HStack>

            <DataTable
              data={filteredStudents}
              columns={leaderboardColumns}
              loading={isLoading}
              emptyState={
                <VStack spacing={4} py={12}>
                  <FiAward size={48} opacity={0.4} />
                  <div style={{ textAlign: 'center' }}>
                    <p
                      style={{
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        marginBottom: '0.5rem',
                      }}
                    >
                      No student data available
                    </p>
                    <p style={{ opacity: 0.8 }}>
                      {activeSemester
                        ? 'No students found for the selected filters'
                        : 'Select a semester to view student performance'}
                    </p>
                  </div>
                </VStack>
              }
            />
          </VStack>
        </Card>

        {/* Performance Distribution */}
        {filteredStudents.length > 0 && (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Card padding={6} variant="outline">
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="600">
                  Performance Distribution
                </Text>
                {[
                  {
                    level: 'excellent',
                    label: 'Excellent (90%+)',
                    color: 'green',
                  },
                  { level: 'good', label: 'Good (75-89%)', color: 'blue' },
                  {
                    level: 'average',
                    label: 'Average (50-74%)',
                    color: 'yellow',
                  },
                  {
                    level: 'needs_improvement',
                    label: 'Needs Improvement (<50%)',
                    color: 'red',
                  },
                ].map(({ level, label, color }) => {
                  const count = filteredStudents.filter(
                    (s) => s.performance_level === level,
                  ).length;
                  const percentage = (count / filteredStudents.length) * 100;

                  return (
                    <HStack key={level} justify="space-between">
                      <Text fontSize="sm">{label}</Text>
                      <HStack>
                        <Text fontSize="sm" fontWeight="600">
                          {count}
                        </Text>
                        <Progress
                          value={percentage}
                          size="sm"
                          colorScheme={color as any}
                          minW="60px"
                        />
                      </HStack>
                    </HStack>
                  );
                })}
              </VStack>
            </Card>

            <Card padding={6} variant="outline">
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="600">
                  Top 5 Performers
                </Text>
                {filteredStudents.slice(0, 5).map((student, index) => (
                  <HStack
                    key={student.student_token}
                    justify="space-between"
                    p={2}
                    bg="gray.50"
                    borderRadius="md"
                  >
                    <HStack>
                      <Text fontSize="sm" fontWeight="600">
                        #{index + 1}
                      </Text>
                      <Avatar
                        size="xs"
                        name={`${student.first_name} ${student.last_name}`}
                      />
                      <Text fontSize="sm">
                        {student.first_name} {student.last_name}
                      </Text>
                    </HStack>
                    <Badge colorScheme="green" size="sm">
                      {student.percent_completed?.toFixed(2)}%
                    </Badge>
                  </HStack>
                ))}
              </VStack>
            </Card>
          </SimpleGrid>
        )}
      </VStack>
    </AdminLayout>
  );
};
